import React from 'react';

import {assertDataViewActionIsConstant} from 'analysis-tool/data-view/DataViewContext';
import {OverflowDataViewAction} from 'analysis-tool/data-view/DataViewContext';
import {trackEvent} from 'analytics/Analytics';
import {pluralize} from 'BenchlingUtil';
import {ActionOrigin, DATA_FRAMES_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {DataFrame} from 'data-frames/DataFrameTypes';
import * as DataFrameDAO from 'data-frames/network/DataFrameDAO';
import {DataFrameTableMode} from 'data-frames/table/DataFrameTableConstants';
import makeDataFrameExportString from 'data-frames/util/makeDataFrameExportString';
import exportWithNotification from 'exports/exportWithNotification';
import Option from 'ui-library/menus/Option';
import {initiateTextDownload} from 'util/TextToBlobUtil';

import {OverflowActionRenderFn, ToolbarActionRenderParams} from '../DataFrameActionToolbar';

function downloadDataFrameFromServer(dataFrameId: string): void {
  exportWithNotification(async () => await DataFrameDAO.exportDataFrameAsCSV(dataFrameId));
}

function downloadDataFrameFromClient(
  dataFrame: DataFrame,
  selectedRowIds: Set<string>,
  tableMode: DataFrameTableMode
): void {
  initiateTextDownload(
    makeDataFrameExportString(dataFrame, selectedRowIds, tableMode),
    `${dataFrame.name}.csv`.replace(/\//g, '-')
  );
}

/**
 * TODO(BNCH-123685): Move to analysis-tool/data-view/ module once `renderDownloadAsCSVOption` is removed.
 */
export function getDownloadAsCSVAction(
  dataFrame: DataFrame,
  analyticsData: {
    dataFrameId?: string;
    actionOrigin: ActionOrigin;
  },
  selectedRowIds: Set<string>
): OverflowDataViewAction {
  const numSelectedRows = selectedRowIds.size;
  return {
    icon: 'download',
    label:
      numSelectedRows === 0
        ? 'Download as CSV'
        : `Download ${numSelectedRows} ${pluralize(numSelectedRows, 'row')} as CSV`,
    onClick: () => {
      // If there is an ID, then a PersistedDataFrame exists and we can export all of its data on the server.
      // Otherwise, the data will only be available on the client.
      // Additionally, if a user has selected rows, we can also just export in the client, since the
      // selected rows must be on the client.
      if (analyticsData.dataFrameId != null && selectedRowIds.size === 0) {
        downloadDataFrameFromServer(analyticsData.dataFrameId);
      } else {
        // Download as TEXT to be consistent with server downloads, and to avoid a dependency on hydrated properties.
        downloadDataFrameFromClient(dataFrame, selectedRowIds, DataFrameTableMode.TEXT);
      }
      trackEvent(DATA_FRAMES_ANALYTICS_ACTIONS.EXPORTED_DATA_FRAME, {
        data_frame_id: analyticsData.dataFrameId,
        action_origin: analyticsData.actionOrigin,
        type: 'file-csv',
        num_rows_selected: selectedRowIds.size,
        total_columns: dataFrame.columns.length,
        total_rows: dataFrame.rows.length,
      });
    },
  };
}

/**
 * @deprecated TODO(BNCH-123685): Remove once uses of DataFrameTableWrapper are removed.
 */
export const renderDownloadAsCSVOption: OverflowActionRenderFn = (
  dataFrame: DataFrame,
  params: Pick<ToolbarActionRenderParams, 'dataFrameId' | 'selectedRowIds' | 'actionOrigin'>
) => {
  const option = getDownloadAsCSVAction(
    dataFrame,
    {
      dataFrameId: params.dataFrameId,
      actionOrigin: params.actionOrigin,
    },
    params.selectedRowIds
  );
  assertDataViewActionIsConstant(option);

  return (
    <Option icon={option.icon} key="DownloadAsCSVOption" onSelect={option.onClick}>
      {option.label}
    </Option>
  );
};
